import baseRule from '../../../CRUD/namespace/relation/willUpdate_namespace_relation';
import {constants} from 'lib_ui-services';
import { errors } from 'lib_ui-primitives';
import {
    canTransition,
    STATUS_SUSPENDED,
    STATUS_CLOSED,
    STATUS_IN_PROGRESS,
    STATUS_CLOSED_SHORT,
    STATUS_CLOSED_OVER
} from './orderStatus';

export default {
    verb: 'willUpdate',
    namespace: 'order',
    relation: 'order',
    // Run before the generic willUpdate so the injected status change shows up
    // in the diff it computes — otherwise the generic would throw
    // "There are no changes to save."
    priority: baseRule.priority + 10,
    description: 'Inject status changes for the start/suspend/close buttons',
    logic
};

// 'qualitycheck' is deliberately absent: Quality Check was removed in #1909, so a button
// left behind in tenant metadata falls through the guard below and is inert rather than
// stamping a field nothing reads.
const HANDLED_TYPES = ['suspend', 'close', 'start'];

async function logic({ data, context, dispatch }) {
    const type = context.type?.toLowerCase();
    if (!HANDLED_TYPES.includes(type)) {
        return { data, context };
    }

    // The suspend/close buttons publish `{ record }` rather than going through
    // the form state machine, so `data.oldRecord` / `data.newRecord` are absent.
    // Seed both from `record` so the generic willUpdate can diff only the
    // fields we actually changed.
    const oldRecord = data.oldRecord ?? data.record ?? data.activeRecord?.record;
    const newRecord = { ...(data.newRecord ?? data.record ?? data.activeRecord?.record) };

    // Without an order record there is nothing to transition, and carrying on hands the generic
    // willUpdate a change with no `_id` to address — it would send it as a PATCH to
    // `/api/order/order/undefined`. Throw rather than return: a rule that returns nothing passes the
    // original data on to the generic, whereas a throw stops the workflow before the generic runs.
    if (oldRecord == null || newRecord._id == null) {
        const message = 'Cannot update the order: no order record was found.';
        popError(dispatch, message);
        throw new errors.ValidationError(message, {});
    }

    // An order can be persisted with an `order:status` that carries no title (#2026). The server
    // rule falls back to 'New'; without the same fallback here the client refuses a transition the
    // server would have accepted, leaving the order a dead end.
    const currentStatus = oldRecord['order:status']?.title || 'New';

    if (type === 'suspend') {
        if (!canTransition(currentStatus, 'Suspended')) {
            popError(dispatch, `Cannot suspend an order with status '${currentStatus}'.`);
            return;
        }
        newRecord['order:status'] = { ...STATUS_SUSPENDED };
    } else if (type === 'close') {

        const overScanned = oldRecord['overScanned'];
        const remaining = oldRecord['remaining'];

        if (overScanned > 0) {
            if (!canTransition(currentStatus, 'Closed-Over')) {
                if (/closed/i.test(currentStatus)) {
                    popError(dispatch, 'Order is already Closed.');
                } else {
                    popError(dispatch, `Cannot change an order with status '${currentStatus}' to 'Closed-Over'.`);
                }
                return;
            }
            newRecord['order:status'] = { ...STATUS_CLOSED_OVER };
        } else if (remaining > 0) {
            if (!canTransition(currentStatus, 'Closed-Short')) {
                if (/closed/i.test(currentStatus)) {
                    popError(dispatch, 'Order is already Closed.');
                } else {
                    popError(dispatch, `Cannot change an order with status '${currentStatus}' to 'Closed-Short'.`);
                }
                return;
            }
            newRecord['order:status'] = {...STATUS_CLOSED_SHORT};
        } else {
            if (!canTransition(currentStatus, 'Closed')) {
                if (/closed/i.test(currentStatus)) {
                    popError(dispatch, 'Order is already Closed.');
                } else {
                    popError(dispatch, `Cannot close an order with status '${currentStatus}'.`);
                }
                return;
            }
            newRecord['order:status'] = { ...STATUS_CLOSED };
        }
        newRecord.closedTime = new Date();
        newRecord.closedReason = 'Manually closed';
    } else if (type === 'start') {
        if (!canTransition(currentStatus, 'In Progress')) {
            popError(dispatch, `Cannot start an order with status '${currentStatus}'.`);
            return;
        }
        newRecord['order:status'] = {...STATUS_IN_PROGRESS};
    }

    return { data: { ...data, newRecord, oldRecord }, context };
}

function popError(dispatch, message) {
    dispatch(
        {
            message,
            timeout: 5000,
            variant: 'info',
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}
